import React from 'react';
import { Card } from './ui/card';
import { images } from '../data/mock';
import { Wine, UtensilsCrossed, Music, Sparkles, Pizza } from 'lucide-react';

// Stable across renders (react-hooks/purity)
const EVENT_PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  key: i,
  cls: i % 3 === 0 ? 'bg-pink-400/30' : i % 3 === 1 ? 'bg-orange-400/30' : 'bg-yellow-400/30',
  style: {
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 5}s`,
    animationDuration: `${8 + Math.random() * 8}s`,
  }
}));

const CONFIRMED_FOOD_VENDORS = [
  'La Piña Cantina',
  'Shaka Mex',
  'Taqueria La Marea',
  '1311',
  'Pitch Sports Bar'
];

const AtTheEventSection = () => {
  const highlights = [
    {
      icon: Wine,
      title: "Drink",
      description: "Sip your way through a world of agave. Taste exotic tequilas and pure agave spirits — from additive‑free gems and luxurious extra añejos to bacanora, sotol, raicilla, and more. Then elevate the experience with handcrafted cocktails mixed just for you by some of Hawaii's premier bartenders and master mixologists.",
      color: "pink",
      image: images.classicCocktail
    },
    {
      icon: UtensilsCrossed,
      title: "Food",
      description: "Plus, dive into sabor auténtico with savory Mexican‑inspired dishes crafted by Hawaii's top chefs and most beloved restaurants. From street‑style tacos and bold salsas to creative island‑meets‑México mashups, every bite is a celebration of flavor. Come hungry and explore a curated lineup of must‑taste plates, all made to pair perfectly with your favorite agave spirits.",
      color: "orange",
      image: images.tacos
    },
    {
      icon: Music,
      title: "Entertainment",
      description: "Dance under the Waikiki sky to the vibes of Hawaii's hottest DJs, spinning feel‑good tracks that keep the energy high all night long — including a special tribute set honoring the legendary SuperCW. Then get swept up in the sounds of a live mariachi band, surprise performances, and unforgettable moments you'll be talking about long after the last song fades. This isn't just a party — it's a full‑on fiesta for your senses.",
      color: "yellow",
      image: images.mariachi
    }
  ];

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 relative overflow-hidden">
      {/* Animated background elements - hidden on small screens for performance */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
        <div className="absolute top-20 left-10 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Floating particles - reduced on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
        {EVENT_PARTICLES.map((p) => (
          <div
            key={p.key}
            className={`absolute w-2 h-2 rounded-full animate-float ${p.cls}`}
            style={p.style}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500/20 via-orange-500/20 to-yellow-500/20 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-pink-500/30 mb-4 sm:mb-6">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-pink-400 animate-pulse" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 font-bold tracking-wider text-xs sm:text-sm">EXPERIENCE THE FIESTA</span>
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 animate-pulse" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 sm:mb-4">
            At the Event
          </h2>
          <p className="text-base sm:text-xl text-white/70 max-w-2xl mx-auto px-4">
            An unforgettable evening of spirits, flavors, and celebration
          </p>
        </div>

        {/* Highlight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {highlights.map((item, index) => (
            <Card 
              key={index}
              className="group bg-white/5 backdrop-blur-sm border-white/10 rounded-3xl overflow-hidden hover:border-pink-500/30 transition-all duration-500 hover:transform hover:scale-[1.02] hover:shadow-2xl hover:shadow-pink-500/10"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent" />
                
                {/* Icon Badge */}
                <div className={`absolute bottom-4 left-4 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${
                  item.color === 'pink' ? 'bg-gradient-to-br from-pink-500 to-pink-600 shadow-pink-500/30' :
                  item.color === 'orange' ? 'bg-gradient-to-br from-orange-500 to-orange-600 shadow-orange-500/30' :
                  'bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-yellow-500/30'
                }`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                <h3 className={`text-xl sm:text-2xl font-black mb-2 sm:mb-3 ${
                  item.color === 'pink' ? 'text-pink-400' :
                  item.color === 'orange' ? 'text-orange-400' :
                  'text-yellow-400'
                }`}>
                  {item.title}
                </h3>
                <p className="text-white/80 leading-relaxed text-sm sm:text-base">
                  {item.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Confirmed Food Partners */}
        <div className="mt-12 sm:mt-16" data-testid="confirmed-food-vendors">
          <div className="bg-gradient-to-br from-orange-500/10 via-pink-500/5 to-transparent border border-orange-500/25 rounded-3xl p-6 sm:p-8 backdrop-blur-sm">
            <div className="flex items-center justify-center gap-2 mb-5">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Pizza className="w-5 h-5 text-white" />
              </div>
              <div className="text-center">
                <p className="text-orange-300 text-xs sm:text-sm font-bold tracking-wider uppercase">Confirmed Food Partners</p>
                <h3 className="text-xl sm:text-2xl font-black text-white">Tasting plates from these spots</h3>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4">
              {CONFIRMED_FOOD_VENDORS.map((name) => (
                <span
                  key={name}
                  data-testid={`food-vendor-${name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                  className="inline-flex items-center gap-1.5 bg-white/8 hover:bg-white/12 border border-white/15 text-white/90 font-semibold text-sm sm:text-base px-4 py-2 rounded-full transition-colors"
                >
                  <UtensilsCrossed className="w-3.5 h-3.5 text-orange-400" />
                  {name}
                </span>
              ))}
              <span
                data-testid="food-vendor-more-to-come"
                className="inline-flex items-center gap-1.5 bg-gradient-to-r from-pink-500/15 to-orange-500/15 border border-pink-500/40 text-pink-200 font-bold text-sm sm:text-base px-4 py-2 rounded-full"
              >
                <Sparkles className="w-3.5 h-3.5" />
                More to come…
              </span>
            </div>

            <p className="text-white/55 text-xs sm:text-sm text-center max-w-2xl mx-auto">
              Your ticket includes 3 tasting plates from your choice of participating restaurants. Add 4 extra restaurant tasting tickets for $10 at checkout.
            </p>
          </div>
        </div>

        {/* Bottom Accent */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-4 text-white/60">
            <span className="w-12 h-px bg-gradient-to-r from-transparent to-pink-500/50" />
            <span className="text-2xl">🍹🌮🎶</span>
            <span className="w-12 h-px bg-gradient-to-l from-transparent to-yellow-500/50" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AtTheEventSection;
